<template>
  <div
    class="safe-image-container"
    :class="containerClass"
    :style="containerStyle"
  >
    <!-- 正常图片 -->
    <img
      v-show="!isLoading && !isError && actualSrc"
      ref="imageRef"
      :src="actualSrc"
      :alt="alt"
      :class="imageClass"
      :style="computedImageStyle"
      :loading="lazy ? 'lazy' : 'eager'"
      @load="handleLoad"
      @error="handleError"
    />
  </div>
</template>

<script>
export default {
  name: "SafeImage",
  props: {
    // 图片地址
    src: {
      type: String,
      default: "",
    },
    // 图片描述
    alt: {
      type: String,
      default: "图片",
    },
    // 占位图（加载中或加载失败时显示）
    placeholder: {
      type: String,
      default: "",
    },
    // 错误占位图（加载失败时显示，优先级高于 placeholder）
    errorPlaceholder: {
      type: String,
      default: "",
    },
    // 容器类名
    containerClass: {
      type: [String, Object, Array],
      default: "",
    },
    // 图片类名
    imageClass: {
      type: [String, Object, Array],
      default: "",
    },
    // 容器样式
    containerStyle: {
      type: [Object, String],
      default: () => ({}),
    },
    // 图片样式
    imageStyle: {
      type: [Object, String],
      default: () => ({}),
    },
    // 是否显示占位图
    showPlaceholder: {
      type: Boolean,
      default: true,
    },
    // 是否显示错误文本
    showErrorText: {
      type: Boolean,
      default: false,
    },
    // 是否可重试
    retryable: {
      type: Boolean,
      default: false,
    },
    // 是否懒加载
    lazy: {
      type: Boolean,
      default: false,
    },
    // 图片对象适应方式
    fit: {
      type: String,
      default: "cover", // cover | contain | fill | none | scale-down
      validator: (value) => {
        return ["cover", "contain", "fill", "none", "scale-down"].includes(
          value
        );
      },
    },
    // 默认占位图（当 src 为空时使用）
    defaultPlaceholder: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isLoading: false,
      isError: false,
      retryCount: 0,
      maxRetryCount: 3,
    };
  },
  created() {
    this._preloader = null;
  },
  computed: {
    // 实际显示的图片地址
    actualSrc() {
      if (!this.src) {
        // 如果 src 为空，使用默认占位图或空字符串
        return this.defaultPlaceholder || "";
      }
      return this.src;
    },
    // 占位图样式
    placeholderStyle() {
      const style = {};
      if (this.placeholder) {
        style.backgroundImage = `url(${this.placeholder})`;
        style.backgroundSize = "cover";
        style.backgroundPosition = "center";
        style.backgroundRepeat = "no-repeat";
      }
      return style;
    },
    // 错误占位图样式
    errorStyle() {
      const style = {};
      if (this.errorPlaceholder) {
        style.backgroundImage = `url(${this.errorPlaceholder})`;
        style.backgroundSize = "cover";
        style.backgroundPosition = "center";
        style.backgroundRepeat = "no-repeat";
      }
      return style;
    },
    // 图片样式（包含 fit 属性）
    computedImageStyle() {
      const style = { ...this.imageStyle };
      if (this.fit) {
        style.objectFit = this.fit;
      }
      return style;
    },
  },
  watch: {
    src: {
      handler(newSrc) {
        if (newSrc) {
          this.reset();
          this.loadImage();
        } else {
          // src 为空时，直接显示占位图
          this.isLoading = false;
          this.isError = true;
        }
      },
      immediate: true,
    },
  },
  mounted() {},
  beforeDestroy() {
    this.cleanupPreloader();
  },
  methods: {
    /**
     * 清理预加载对象防止竞态
     */
    cleanupPreloader() {
      if (this._preloader) {
        this._preloader.onload = null;
        this._preloader.onerror = null;
        this._preloader.src = ""; // 停止旧图片的下载
        this._preloader = null;
      }
    },
    /**
     * 加载图片
     */
    loadImage() {
      if (!this.actualSrc) {
        this.isError = true;
        this.isLoading = false;
        return;
      }

      // 1. 立即清理上一次的预加载器
      this.cleanupPreloader();

      // 2. 如果 DOM 已经存在且已完成（比如缓存），直接处理
      if (this.$refs.imageRef && this.$refs.imageRef.complete) {
        const img = this.$refs.imageRef;
        if (img.naturalWidth === 0) {
          this.handleError();
        } else {
          this.handleLoad();
        }
        return;
      }

      // 3. 开始新的加载流程
      this.isLoading = true;
      this.isError = false;
      const src = this.actualSrc;
      this._preloader = new Image();
      this._preloader.onload = () => {
        if (src === this.actualSrc) {
          this.handleLoad();
          this.cleanupPreloader();
        }
      };
      this._preloader.onerror = (error) => {
        if (src === this.actualSrc) {
          this.handleError();
          this.cleanupPreloader();
        }
      };
      this._preloader.src = src;
    },

    /**
     * 图片加载成功
     */
    handleLoad() {
      this.isLoading = false;
      this.isError = false;
      this.retryCount = 0;
      this.$emit("load", this.actualSrc);
    },

    /**
     * 图片加载失败
     */
    handleError() {
      this.isLoading = false;
      this.isError = true;
      this.$emit("error", this.actualSrc);
    },

    /**
     * 重试加载
     */
    handleRetry() {
      if (this.retryCount >= this.maxRetryCount) {
        console.warn(
          `[SafeImage] 图片加载失败，已重试 ${this.maxRetryCount} 次，停止重试`
        );
        return;
      }

      this.retryCount++;
      this.reset();
      this.loadImage();
      this.$emit("retry", this.retryCount);
    },

    /**
     * 重置状态
     */
    reset() {
      this.isLoading = true;
      this.isError = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.safe-image-container {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #f5f7fa;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }

  .safe-image-placeholder,
  .safe-image-error {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #f5f7fa;
    color: #c0c4cc;
  }

  .safe-image-placeholder {
    .loading-icon {
      width: 40px;
      height: 40px;
      animation: rotate 1s linear infinite;

      .loading-svg {
        width: 100%;
        height: 100%;
        color: #c0c4cc;
      }
    }
  }

  .safe-image-error {
    cursor: default;

    .error-icon {
      width: 48px;
      height: 48px;
      margin-bottom: 8px;

      .error-svg {
        width: 100%;
        height: 100%;
        color: #c0c4cc;
      }
    }

    .error-text {
      font-size: 12px;
      color: #909399;
      margin: 0 0 8px 0;
    }

    .retry-btn {
      padding: 4px 12px;
      font-size: 12px;
      color: #409eff;
      background-color: #ecf5ff;
      border: 1px solid #b3d8ff;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        color: #ffffff;
        background-color: #409eff;
        border-color: #409eff;
      }

      &:active {
        opacity: 0.8;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
